import formatDate from 'date-fns/format';
import type { VoidFunctionComponent } from 'react';

import { BannerAttention, BannerSize, BannerStyle } from '../../../../typings/enums';
import Banner from '../../generic/Banner';
import { NavigationButton } from '../../generic/Button';

/** Revisions analyzed on or after this date already land in Sparkle, so the card doesn't apply. */
const SPARKLE_CUTOFF = new Date('2026-06-01T00:00:00Z');

/**
 * The card only applies to revisions that have been analyzed, and only when that
 * analysis predates Sparkle. Written as a type predicate so call sites can pass
 * the narrowed date straight through to the card.
 */
export const shouldShowAnalyzedWithoutSparkleCard = (
  revisionAnalyzedAt: string | Date | null | undefined,
): revisionAnalyzedAt is string | Date =>
  !!revisionAnalyzedAt && new Date(revisionAnalyzedAt) < SPARKLE_CUTOFF;

type Params = {
  /** When the revision was last analyzed (`Revision.dependency_cache_valid`). */
  revisionAnalyzedAt: string | Date;
};

export const AnalyzedWithoutSparkleCard: VoidFunctionComponent<Params> = ({ revisionAnalyzedAt }) => {
  const analyzedDate = new Date(revisionAnalyzedAt);
  const bodyMessage = `This revision was last analyzed on ${formatDate(analyzedDate, 'MM/dd/yyyy')} and its dependencies are not yet included in FOSSA's new dependency database.`;
  const action = (
    <NavigationButton
      type="error"
      size="medium"
      to="https://docs.fossa.com/docs/help/dependencylocks-deprecation"
      openInNewTab
    >
      Learn More
    </NavigationButton>
  );
  return (
    <Banner
      bannerSize={BannerSize.Medium}
      bannerStyle={BannerStyle.High}
      bannerAttention={BannerAttention.Low}
      title="Action required: re-analyze this revision to preserve historical dependency data"
      body={bodyMessage}
      actions={action}
    />
  );
};
